/**
 * Панель карточки: критерии приёмки, этапы, комментарии, session-context.
 *
 * Flow: доска открывает панель по id → панель дочитывает карточку целиком
 *       (список не отдаёт этапы и комментарии) → действия пользователя уходят
 *       в API → onChanged просит App обновить доску.
 *
 * Зависимости:
 *   - react: локальное состояние формы и загрузки;
 *   - ./api: операции над карточкой;
 *   - ./model: набор этапов и их статусов.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  addComment, addTestRun, archiveItem, getContext, getItem, listTestRuns, putContext, setStage, takeItem,
} from './api.js'
import { REQUIRED_STAGES, STAGE_STATUSES } from './model.js'

export default function ItemPanel({ id, onClose, onChanged, readOnly }) {
  const [item, setItem] = useState(null)
  const [context, setContext] = useState('')
  const [comment, setComment] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [testRuns, setTestRuns] = useState([])
  const [runForm, setRunForm] = useState({ kind: 'unit', result: 'passed', summary: '' })

  const reload = useCallback(async () => {
    try {
      const [full, ctx, runs] = await Promise.all([getItem(id), getContext(id), listTestRuns(id)])
      setItem(full)
      setContext(ctx.content || '')
      setTestRuns(runs.items || [])
    } catch (e) {
      setError(e.message)
    }
  }, [id])

  useEffect(() => {
    reload()
  }, [reload])

  async function act(fn) {
    setBusy(true)
    setError('')
    try {
      await fn()
      await reload()
      onChanged()
    } catch (e) {
      setError(e.missingStages ? `${e.message}: ${e.missingStages.join(', ')}` : e.message)
    } finally {
      setBusy(false)
    }
  }

  if (!item) {
    return (
      <div className="overlay" onClick={onClose}>
        <div className="panel" onClick={(e) => e.stopPropagation()}>
          <p className="muted">{error || 'Загрузка…'}</p>
        </div>
      </div>
    )
  }

  // Обязательные этапы показываем всегда, даже если их ещё нет в карточке:
  // иначе непонятно, чего именно не хватает для закрытия.
  const stageKeys = [
    ...REQUIRED_STAGES,
    ...(item.stages || []).map((s) => s.key).filter((k) => !REQUIRED_STAGES.includes(k)),
  ]

  return (
    <div className="overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2>{item.title}</h2>
          <button className="btn ghost" onClick={onClose}>Закрыть</button>
        </div>
        <p className="muted">
          {item.id} · {item.type} · {item.priority} · {item.status}
          {item.assignee ? ` · ${item.assignee}` : ''}
          {item.branch ? ` · ${item.branch}` : ''}
        </p>
        {error && <div className="error">{error}</div>}

        {item.value && (
          <section>
            <h3>Ценность</h3>
            <p style={{ fontSize: 13, margin: 0 }}>{item.value}</p>
          </section>
        )}

        <section>
          <h3>Критерии приёмки</h3>
          {item.acceptance?.length ? (
            <ul>{item.acceptance.map((a, i) => <li key={i}>{a}</li>)}</ul>
          ) : (
            <p className="muted">Не заданы — карточка без проверяемых критериев не карточка</p>
          )}
        </section>

        <section>
          <h3>Этапы</h3>
          {stageKeys.map((key) => {
            const stage = (item.stages || []).find((s) => s.key === key)
            return (
              <div className="stage" key={key}>
                <span className="key">{key}</span>
                <select
                  disabled={readOnly || busy}
                  value={stage?.status || 'pending'}
                  onChange={(e) => act(() => setStage(item.id, key, e.target.value,
                    stage?.kind || (REQUIRED_STAGES.includes(key) ? 'phase' : 'test')))}
                >
                  {STAGE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {REQUIRED_STAGES.includes(key) && <span className="muted">обязательный</span>}
              </div>
            )
          })}
        </section>

        <section>
          <h3>Прогоны проверок</h3>
          {testRuns.length === 0 ? (
            <p className="muted">
              Прогонов нет. Карточку нельзя закрыть, пока не записан хотя бы один —
              если проверять было нечего, запишите прогон с результатом skipped и причиной.
            </p>
          ) : (
            <table className="runs">
              <tbody>
                {testRuns.map((r) => (
                  <tr key={r.id} className={`run-${r.result}`}>
                    <td className="run-kind">{r.kind}</td>
                    <td className="run-result">{r.result}</td>
                    <td>{r.summary}</td>
                    <td className="run-at">{new Date(r.ran_at).toLocaleString('ru-RU',
                      { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!readOnly && (
            <div className="run-form">
              <input placeholder="вид: unit, integration, smoke, negative…"
                     value={runForm.kind}
                     onChange={(e) => setRunForm({ ...runForm, kind: e.target.value })} />
              <select value={runForm.result}
                      onChange={(e) => setRunForm({ ...runForm, result: e.target.value })}>
                <option value="passed">passed</option>
                <option value="failed">failed</option>
                <option value="skipped">skipped</option>
              </select>
              <input placeholder="итог: что прогонялось, сколько прошло, что упало"
                     value={runForm.summary}
                     onChange={(e) => setRunForm({ ...runForm, summary: e.target.value })} />
              <button className="btn" disabled={busy}
                      onClick={() => act(async () => {
                        await addTestRun(item.id, runForm.kind, runForm.result, runForm.summary)
                        setRunForm({ ...runForm, summary: '' })
                      })}>
                Записать прогон
              </button>
            </div>
          )}
        </section>

        <section>
          <h3>Контекст сессии</h3>
          <textarea
            value={context}
            disabled={readOnly}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Сделано / Осталось / Решения и почему / Файлы и ветка / Как проверить / Следующий шаг"
          />
          {!readOnly && (
            <div className="row" style={{ marginTop: 8 }}>
              <button className="btn" disabled={busy} onClick={() => act(() => putContext(item.id, context))}>
                Сохранить контекст
              </button>
            </div>
          )}
        </section>

        <section>
          <h3>Комментарии ({item.comments?.length || 0})</h3>
          {(item.comments || []).map((c, i) => (
            <div className="comment" key={i}>
              <div className="who">{c.by} · {new Date(c.at).toLocaleString('ru-RU')}</div>
              <div className="body">{c.body}</div>
            </div>
          ))}
          {!readOnly && (
            <div className="row">
              <input
                value={comment}
                placeholder="Добавить комментарий"
                onChange={(e) => setComment(e.target.value)}
              />
              <button
                className="btn"
                disabled={busy || !comment.trim()}
                onClick={() => act(async () => {
                  await addComment(item.id, comment)
                  setComment('')
                })}
              >
                Отправить
              </button>
            </div>
          )}
        </section>

        {(item.depends_on?.length > 0 || item.linked_tasks?.length > 0
          || item.parent || item.children?.length > 0) && (
          <section>
            <h3>Связи</h3>
            {item.parent && (
              <p style={{ fontSize: 13, margin: '0 0 6px' }}>
                Часть работы над: {item.parent}
              </p>
            )}
            {item.children?.length > 0 && (
              <p style={{ fontSize: 13, margin: '0 0 6px' }}>
                Прикреплено работ: {item.children.length} — {item.children.join(', ')}
              </p>
            )}
            {item.depends_on?.length > 0 && (
              <p style={{ fontSize: 13, margin: '0 0 6px' }}>Зависит от: {item.depends_on.join(', ')}</p>
            )}
            {item.linked_tasks?.length > 0 && (
              <p style={{ fontSize: 13, margin: 0 }}>Задачи: {item.linked_tasks.join(', ')}</p>
            )}
          </section>
        )}

        {!readOnly && (
          <section className="row">
            {item.status === 'backlog' && (
              <button className="btn" disabled={busy} onClick={() => act(() => takeItem(item.id))}>
                Взять в работу
              </button>
            )}
            <button className="btn ghost" disabled={busy} onClick={() => act(() => archiveItem(item.id))}>
              В архив
            </button>
          </section>
        )}
      </div>
    </div>
  )
}
